import { useTranslation } from 'react-i18next'
import { KILO } from '../../constants/KILO_THEME'

type RecoveryDebtCardProps = {
  debtScore: number
  consecutiveDays: number
  trend: 'accumulating' | 'recovering' | 'stable'
  hasMetricData?: boolean
  embedded?: boolean
}

const RecoveryDebtCard = ({
  debtScore,
  consecutiveDays,
  trend,
  hasMetricData = true,
  embedded = false,
}: RecoveryDebtCardProps) => {
  const { t } = useTranslation()

  const spanClass = embedded ? '' : 'col-span-2 '

  if (!hasMetricData) {
    return (
      <div className={`${spanClass}rounded-2xl border border-dashed border-kilo-border bg-kilo-card p-5`}>
        <p className={`mb-2 ${KILO.sectionLabel}`}>{t('coach.recoveryDebt.title')}</p>
        <p className={`text-sm leading-relaxed ${KILO.text.soft}`}>{t('coach.recoveryDebt.emptyState')}</p>
      </div>
    )
  }

  const isInDebt = debtScore > 10
  const isRecovered = debtScore < -10
  const statusColor = isInDebt
    ? debtScore > 60
      ? 'text-kilo-danger'
      : 'text-orange-400'
    : isRecovered
      ? 'text-kilo-accent'
      : KILO.text.muted
  const statusLabel = isInDebt
    ? debtScore > 60
      ? t('coach.recoveryDebt.severeDebt')
      : t('coach.recoveryDebt.inDebt')
    : isRecovered
      ? t('coach.recoveryDebt.recovered')
      : t('coach.recoveryDebt.balanced')
  const barWidth = Math.min(100, Math.max(0, (debtScore + 50) / 1.5))

  const trendLabel =
    trend === 'accumulating'
      ? t('coach.recoveryDebt.trendWorse')
      : trend === 'recovering'
        ? t('coach.recoveryDebt.trendImproving')
        : t('coach.recoveryDebt.trendStable')

  return (
    <div className={`${spanClass}rounded-2xl border border-kilo-border bg-kilo-card p-5`}>
      <div className="mb-4">
        <p className={`${KILO.sectionLabel} ${KILO.text.soft}`}>{t('coach.recoveryDebt.title')}</p>
      </div>
      <div className={`relative mb-4 h-2 rounded-full ${KILO.bg.nested}`}>
        <div
          className={`absolute left-0 top-0 h-2 rounded-full transition-all duration-700 ${
            isInDebt ? 'bg-orange-500' : 'bg-kilo-accent'
          }`}
          style={{ width: `${barWidth}%` }}
        />
        <div className="absolute top-[-2px] h-3 w-0.5 bg-kilo-line" style={{ left: '33%' }} />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-xl font-semibold ${statusColor}`}>{statusLabel}</span>
          {consecutiveDays > 2 ? (
            <p className={`mt-1.5 text-xs leading-relaxed ${KILO.text.soft}`}>
              {isInDebt
                ? t('coach.recoveryDebt.daysAccumulating', { count: consecutiveDays })
                : t('coach.recoveryDebt.daysRecovering', { count: consecutiveDays })}
            </p>
          ) : null}
        </div>
        <span className={`text-xs ${KILO.text.soft}`}>{trendLabel}</span>
      </div>
    </div>
  )
}

export default RecoveryDebtCard
